import React from "react";

import { Input } from "../../../../../components/input";
import { Label } from "../../../../../components/label";
import { FormErrorText } from "../../../../../components/form/FormErrorText";
import type { QuestionTypeRendererProps } from "./shared";

type NumericQuestionType = "rating" | "scale" | "number";

type NumericQuestionProps<TType extends NumericQuestionType> = QuestionTypeRendererProps<TType> & {
  kind: TType;
};

export function NumericQuestion<TType extends NumericQuestionType>({
  question,
  isPreview,
  scaleLabelErrorMessage,
  onUpdateQuestion,
  kind,
}: NumericQuestionProps<TType>) {
  const { data } = question;
  const updateQuestionData = (updates: Partial<typeof data>) => {
    onUpdateQuestion({
      ...question,
      data: {
        ...data,
        ...updates,
      },
    });
  };

  if (isPreview) {
    if (kind === "rating") {
      return (
        <div className="flex space-x-1">
          {Array.from({ length: data.max_rating || 5 }, (_, i) => (
            <span key={i} className="text-2xl">⭐</span>
          ))}
        </div>
      );
    }

    if (kind === "scale") {
      return (
        <div className="flex items-center space-x-2">
          <span className="text-sm">{data.min_label}</span>
          <div className="flex-1 h-2 bg-gray-200 rounded"></div>
          <span className="text-sm">{data.max_label}</span>
        </div>
      );
    }

    return <Input type="number" placeholder="Enter number" disabled />;
  }

  if (kind === "rating") {
    return (
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Min rating</Label>
        <Input
          type="number"
          value={data.min_rating || 1}
          onChange={(e) =>
            updateQuestionData({ min_rating: Number.parseInt(e.target.value, 10) || 1 })
          }
          className="mt-1"
        />
        </div>
        <div>
          <Label>Max rating</Label>
        <Input
          type="number"
          value={data.max_rating || 5}
          onChange={(e) =>
            updateQuestionData({ max_rating: Number.parseInt(e.target.value, 10) || 5 })
          }
          className="mt-1"
        />
        </div>
      </div>
    );
  }

  if (kind === "scale") {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Min scale</Label>
            <Input
              type="number"
              value={data.min_scale || 1}
              onChange={(e) =>
                updateQuestionData({ min_scale: Number.parseInt(e.target.value, 10) || 1 })
              }
              className="mt-1"
            />
          </div>
          <div>
            <Label>Max scale</Label>
            <Input
              type="number"
              value={data.max_scale || 10}
              onChange={(e) =>
                updateQuestionData({ max_scale: Number.parseInt(e.target.value, 10) || 10 })
              }
              className="mt-1"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Min label</Label>
            <Input
              value={data.min_label || ""}
              onChange={(e) => updateQuestionData({ min_label: e.target.value })}
              className={`mt-1 ${scaleLabelErrorMessage?.minLabel ? "border-red-500 dark:border-red-500" : ""}`}
            />
            <FormErrorText message={scaleLabelErrorMessage?.minLabel} />
          </div>
          <div>
            <Label>Max label</Label>
            <Input
              value={data.max_label || ""}
              onChange={(e) => updateQuestionData({ max_label: e.target.value })}
              className={`mt-1 ${scaleLabelErrorMessage?.maxLabel ? "border-red-500 dark:border-red-500" : ""}`}
            />
            <FormErrorText message={scaleLabelErrorMessage?.maxLabel} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Min value</Label>
        <Input
          type="number"
          value={data.min_value || 0}
          onChange={(e) =>
            updateQuestionData({ min_value: Number.parseFloat(e.target.value) || 0 })
          }
          className="mt-1"
        />
      </div>
      <div>
        <Label>Max value</Label>
        <Input
          type="number"
          value={data.max_value || 1000000}
          onChange={(e) =>
            updateQuestionData({
              max_value: Number.parseFloat(e.target.value) || 1000000,
            })
          }
          className="mt-1"
        />
      </div>
      <div className="grid grid-cols-2 gap-4 col-span-2">
        <div>
          <Label>Decimal places</Label>
          <Input
            type="number"
            value={data.decimal_places || 0}
            onChange={(e) =>
              updateQuestionData({ decimal_places: Number.parseInt(e.target.value, 10) || 0 })
            }
            className="mt-1"
          />
        </div>
        <div>
          <Label>Unit</Label>
          <Input
            value={data.unit || ""}
            onChange={(e) => updateQuestionData({ unit: e.target.value })}
            className="mt-1"
          />
        </div>
      </div>
    </div>
  );
}

export default NumericQuestion;
